// ---------------------------------------------------------------------------
// Mock store — เก็บข้อมูลตัวอย่างทั้งหมดไว้ใน localStorage
// ทุก repository เรียกผ่านไฟล์นี้ที่เดียว เวลาสลับไปใช้ API จริงจะแก้แค่ repository
// ---------------------------------------------------------------------------
import { MOCK_PREFIX, MOCK_LATENCY } from "../services/config";
import { buildSeed, SEED_VERSION } from "./seed";

const VERSION_KEY = `${MOCK_PREFIX}:seed-version`;

const key = (scope, collection) => `${MOCK_PREFIX}:${scope}:${collection}`;

const readRaw = (scope, collection) => {
  try {
    const raw = localStorage.getItem(key(scope, collection));
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

const writeRaw = (scope, collection, rows) => {
  localStorage.setItem(key(scope, collection), JSON.stringify(rows));
};

// เขียน seed ลง localStorage (ครั้งแรก หรือเมื่อกดรีเซ็ต)
export const seedAll = (force = false) => {
  const current = localStorage.getItem(VERSION_KEY);
  if (!force && current === SEED_VERSION) return;

  // ลบข้อมูลเดิมของระบบนี้ทิ้งก่อน
  Object.keys(localStorage)
    .filter((k) => k.startsWith(`${MOCK_PREFIX}:`) && k !== `${MOCK_PREFIX}:lang`)
    .forEach((k) => localStorage.removeItem(k));

  const seed = buildSeed();
  Object.entries(seed).forEach(([scope, collections]) => {
    Object.entries(collections).forEach(([collection, rows]) => writeRaw(scope, collection, rows));
  });
  localStorage.setItem(VERSION_KEY, SEED_VERSION);
};

export const resetAll = () => seedAll(true);

const ensureSeeded = () => seedAll(false);

export const delay = (data) =>
  new Promise((resolve) => setTimeout(() => resolve(typeof data === "function" ? data() : data), MOCK_LATENCY));

export const uid = (prefix = "id") => `${prefix}_${Date.now().toString(36)}${Math.floor(Math.random() * 1e4).toString(36)}`;

// --------------------------------------------------------------------------
// CRUD generic
// --------------------------------------------------------------------------
export const table = (scope, collection) => {
  ensureSeeded();

  const all = () => readRaw(scope, collection) || [];
  const save = (rows) => writeRaw(scope, collection, rows);

  return {
    all,
    save,

    list: (filterFn) => (filterFn ? all().filter(filterFn) : all()),

    find: (id) => all().find((row) => row.id === id) || null,

    create: (payload, prefix = collection.slice(0, 3)) => {
      const rows = all();
      const row = { id: uid(prefix), createdAt: new Date().toISOString(), ...payload };
      rows.unshift(row);
      save(rows);
      return row;
    },

    update: (id, payload) => {
      const rows = all();
      const index = rows.findIndex((row) => row.id === id);
      if (index === -1) return null;
      rows[index] = { ...rows[index], ...payload, updatedAt: new Date().toISOString() };
      save(rows);
      return rows[index];
    },

    remove: (id) => {
      const rows = all().filter((row) => row.id !== id);
      save(rows);
      return true;
    },
  };
};

// helper แบ่งหน้า + ค้นหา ใช้ร่วมกันทุก repository
export const paginate = (rows, { page = 1, perPage = 10, search, searchKeys = [], sortBy, sortDir = "desc" } = {}) => {
  let result = [...rows];

  if (search) {
    const keyword = search.toLowerCase();
    result = result.filter((row) =>
      (searchKeys.length ? searchKeys : Object.keys(row)).some((k) => String(row[k] ?? "").toLowerCase().includes(keyword))
    );
  }

  if (sortBy) {
    result.sort((a, b) => {
      const av = a[sortBy];
      const bv = b[sortBy];
      if (av === bv) return 0;
      const compare = av > bv ? 1 : -1;
      return sortDir === "asc" ? compare : -compare;
    });
  }

  const total = result.length;
  const start = (page - 1) * perPage;
  return { rows: result.slice(start, start + perPage), total, page, perPage };
};

// export ข้อมูลทั้งหมดของ scope ไว้ debug / ดาวน์โหลด
export const exportScope = (scope) => {
  ensureSeeded();
  return Object.keys(localStorage)
    .filter((k) => k.startsWith(`${MOCK_PREFIX}:${scope}:`))
    .reduce((acc, k) => {
      acc[k.split(":").pop()] = JSON.parse(localStorage.getItem(k));
      return acc;
    }, {});
};
